import { UseFormReturn } from "react-hook-form";

import { useNotification } from "@refinedev/core";
import { create } from "zustand";

import { useEditCreated } from "@/components/common/enrichment-async-hint";
import { defineStepper } from "@/components/ui/stepper";
import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import {
  ArtistAIEnrichmentStepType,
  ArtistBasicType,
  ArtistMediaType,
  ArtistSpotifyEnrichmentType,
  createArtistFormSchema,
} from "@/pages/artists/schemas/artist.create.form";
import { SpotifyArtist } from "@/pages/artists/types/spotify-artist";

export const { Stepper, useStepper } = defineStepper(
  {
    id: "selection",
    title: "Artist Selection",
    description: "Select artist from Spotify",
  },
  {
    id: "spotifyEnrichment",
    title: "Spotify Enrichment",
    description: "Enrich artist using Spotify API",
  },
  {
    id: "aiEnrichment",
    title: "AI Enhancement",
    description: "Enrich artist using AI searching the web for information",
  },
  {
    id: "media",
    title: "Media & Shit",
    description: "Upload media, tags and other stuff",
    metadata: {
      index: 0,
    },
  },
);

type State = {
  id?: string;
  selectedSpotifyArtist?: SpotifyArtist;
  basicInfoStep?: ArtistBasicType;
  spotifyEnrichmentStep?: ArtistSpotifyEnrichmentType;
  aiEnrichmentStep?: ArtistAIEnrichmentStepType;
  mediaStep?: ArtistMediaType;
  loading: Record<
    "basicInfo" | "spotifyEnrichment" | "media",
    "idle" | "success" | "loading" | "error"
  >;
};

type Action = {
  setId: (id: string) => void;
  updateSelectedSpotifyArtist: (selected: SpotifyArtist) => void;
  updateSpotifyEnrichment: (enrichment: ArtistSpotifyEnrichmentType) => void;
  updateAIEnrichment: (params: ArtistAIEnrichmentStepType) => void;
  updateMedia: (enrichment: ArtistMediaType) => void;
  updateState: (state: Partial<State["loading"]>) => void;
};

export const useArtistCreateFormStore = create<State & Action>((set) => ({
  mediaStep: {
    id: "",
    hide: false,
    media: {
      photos: [],
      teasers: [],
    },
  },
  setId: (id) => set(() => ({ id })),
  updateSelectedSpotifyArtist: (selected) =>
    set(() => ({
      basicInfoStep: {
        spotifyId: selected.id,
        name: selected.name,
      },
      selectedSpotifyArtist: selected,
    })),
  updateSpotifyEnrichment: (enrichment) => {
    set(() => ({
      spotifyEnrichmentStep: enrichment,
    }));
  },
  updateAIEnrichment: (params) => {
    set(() => ({
      aiEnrichmentStep: params,
    }));
  },
  updateMedia: (media) =>
    set(() => ({
      mediaStep: media,
    })),
  loading: {
    basicInfo: "idle",
    spotifyEnrichment: "idle",
    aiEnrichment: "idle",
    media: "idle",
  },
  updateState: (loading) =>
    set((state) => ({
      loading: { ...state.loading, ...loading },
    })),
}));

export const useArtistCreateForm = () => {
  const methods = useStepper();
  const {
    generateId,
    enrichSpotifyArtist,
    create,
    searchSpotify,
    getSpotifyArtistFromLink,
  } = useArtistRepository();
  const {
    setId,
    updateSelectedSpotifyArtist,
    updateSpotifyEnrichment,
    updateAIEnrichment,
    updateMedia,
    updateState,
    id,
    selectedSpotifyArtist,
    basicInfoStep,
    spotifyEnrichmentStep,
    aiEnrichmentStep,
    mediaStep,
    loading,
  } = useArtistCreateFormStore();
  const { open } = useNotification();
  const editCreated = useEditCreated();

  const onSpotifyArtistSelected = (artist: SpotifyArtist) =>
    updateSelectedSpotifyArtist(artist);

  const onSpotifySearch = (query: string) => searchSpotify(query);
  const onSpotifyLink = async (url: string) => {
    const artist = await getSpotifyArtistFromLink(url);
    if (artist) onSpotifyArtistSelected(artist);
  };

  const onBasicInfoStepSubmit = async (
    values: ArtistBasicType,
    _: UseFormReturn<ArtistBasicType, any, ArtistBasicType>,
  ) => {
    console.debug("Basic Info Step Submitted", values, selectedSpotifyArtist);
    updateState({
      basicInfo: "loading",
    });
    const id = await generateId();
    setId(id);
    console.debug("Generated id", id);

    if (values.spotifyId && selectedSpotifyArtist) {
      try {
        const spotifyEnrichment = await enrichSpotifyArtist(
          values.spotifyId,
          values.name,
          selectedSpotifyArtist,
        );
        console.debug("Spotify Enrichment Response", spotifyEnrichment);
        if (spotifyEnrichment) updateSpotifyEnrichment(spotifyEnrichment);
      } catch (error) {
        updateState({
          basicInfo: "error",
        });
        return;
      }
    }

    updateState({
      basicInfo: "success",
    });

    methods.next();
  };

  const onSpotifyEnrichmentSubmit = () => {
    console.debug("Spotify Enrichment Step Submitted", spotifyEnrichmentStep);
    methods.next();
  };

  const onAiEnrichmentStepSubmit = async (values: {
    instagramScreenshot: string;
  }) => {
    console.debug("AI Enrichment Step Submitted", values);
    updateAIEnrichment({ instagramScreenshot: values.instagramScreenshot });
    methods.next();
  };

  const onMediaStepSubmit = async (values: ArtistMediaType) => {
    console.debug("Media Step Submitted", values);
    updateMedia(values);

    updateState({
      media: "loading",
    });
    await create(
      createArtistFormSchema.parse({
        ...basicInfoStep,
        ...aiEnrichmentStep,
        ...values,
        spotifyEnrichment: spotifyEnrichmentStep,
      }),
    )
      .catch((error) => {
        open?.({
          type: "error",
          message: "Failed to create artist",
          description: error?.response?.data?.detail || error.message,
        });
        throw error;
      })
      .finally(() => {
        updateState({
          media: "success",
        });
      });
    open?.({
      type: "success",
      message: "Artist created successfully",
    });
    // `?created=1` — see `enrichment-async-hint.tsx`. A Tina-deferred create
    // returns before its enrichment job has run.
    editCreated("artists", id!);
  };

  return {
    methods,
    onSpotifySearch,
    onSpotifyLink,
    onSpotifyArtistSelected,
    onBasicInfoStepSubmit,
    onSpotifyEnrichmentSubmit,
    onAiEnrichmentStepSubmit,
    onMediaStepSubmit,
    id,
    selectedSpotifyArtist,
    basicInfoStep,
    spotifyEnrichmentStep,
    aiEnrichmentStep,
    mediaStep,
    updateMedia,
    loading,
  };
};
